import { useState, useRef, useCallback } from 'react';
import ReactCrop, { Crop, PixelCrop, makeAspectCrop, centerCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { X, ZoomIn, ZoomOut, RotateCw, Check } from 'lucide-react';
import React from 'react';

interface ImageCropModalProps {
  imageSrc: string;
  onCropComplete: (croppedFile: File) => void;
  onCancel: () => void;
  aspect?: number | undefined; // Aspect ratio (width/height), undefined for free-form cropping
}

export function ImageCropModal({ imageSrc, onCropComplete, onCancel, aspect }: ImageCropModalProps) {
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    // If aspect is undefined, allow free-form cropping
    if (aspect === undefined) {
      // Start with a crop area that's 80% of the image
      const cropWidth = 80;
      const cropHeight = 80;
      const cropX = (100 - cropWidth) / 2;
      const cropY = (100 - cropHeight) / 2;
      setCrop({
        unit: '%',
        x: cropX,
        y: cropY,
        width: cropWidth,
        height: cropHeight,
      });
    } else {
      // Use aspect ratio locked cropping
      const crop = centerCrop(
        makeAspectCrop(
          {
            unit: '%',
            width: 80, // Start with 80% of the image
          },
          aspect,
          width,
          height
        ),
        width,
        height
      );
      setCrop(crop);
    }
  }, [aspect]);

  const getCroppedImg = useCallback(async (): Promise<File | null> => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      return null;
    }

    const image = imgRef.current;
    const canvas = canvasRef.current;
    const crop = completedCrop;
    const pixelRatio = window.devicePixelRatio || 1;

    // react-image-crop provides crop coordinates in pixels relative to the displayed image
    // Since we apply CSS transforms (scale and rotate), we need to account for these
    // when converting to natural image coordinates and applying the same transforms
    
    // Convert crop coordinates from displayed image space (accounting for CSS scale) to natural image space
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    // The crop coordinates are already in displayed pixels, but CSS scale affects them
    // ReactCrop accounts for CSS transforms, so crop is relative to the transformed display
    // We need to undo the scale transform first
    const cropXUnscaled = crop.x / scale;
    const cropYUnscaled = crop.y / scale;
    const cropWidthUnscaled = crop.width / scale;
    const cropHeightUnscaled = crop.height / scale;
    
    // Convert to natural image coordinates
    const cropXNatural = cropXUnscaled * scaleX;
    const cropYNatural = cropYUnscaled * scaleY;
    const cropWidthNatural = cropWidthUnscaled * scaleX;
    const cropHeightNatural = cropHeightUnscaled * scaleY;

    // Handle rotation: create a canvas with the rotated and scaled image, then crop from it
    const rotateRads = (rotate * Math.PI) / 180;
    const cos = Math.abs(Math.cos(rotateRads));
    const sin = Math.abs(Math.sin(rotateRads));
    
    // Calculate canvas size needed for rotated image (no scale yet, we'll apply it during draw)
    const rotatedNaturalWidth = image.naturalWidth * cos + image.naturalHeight * sin;
    const rotatedNaturalHeight = image.naturalWidth * sin + image.naturalHeight * cos;

    // Create canvas for the fully transformed (rotated + scaled) image
    const transformedCanvas = document.createElement('canvas');
    const transformedWidth = rotatedNaturalWidth * scale;
    const transformedHeight = rotatedNaturalHeight * scale;
    transformedCanvas.width = transformedWidth * pixelRatio;
    transformedCanvas.height = transformedHeight * pixelRatio;
    const transformedCtx = transformedCanvas.getContext('2d');
    
    if (!transformedCtx) return null;

    transformedCtx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    transformedCtx.imageSmoothingQuality = 'high';

    // Draw natural image with rotation and scale applied
    const transformedCenterX = transformedWidth / 2;
    const transformedCenterY = transformedHeight / 2;
    
    transformedCtx.save();
    transformedCtx.translate(transformedCenterX, transformedCenterY);
    transformedCtx.rotate(rotateRads);
    transformedCtx.scale(scale, scale);
    transformedCtx.drawImage(
      image,
      -image.naturalWidth / 2,
      -image.naturalHeight / 2,
      image.naturalWidth,
      image.naturalHeight
    );
    transformedCtx.restore();

    // Calculate crop position in the transformed canvas
    // The crop area in natural coordinates needs to be transformed with the same rotation/scale
    const naturalCenterX = image.naturalWidth / 2;
    const naturalCenterY = image.naturalHeight / 2;
    
    // Crop center relative to natural image center
    const cropCenterX = cropXNatural + cropWidthNatural / 2 - naturalCenterX;
    const cropCenterY = cropYNatural + cropHeightNatural / 2 - naturalCenterY;
    
    // Transform crop center point with rotation and scale
    const transformedCropCenterX = transformedCenterX + (cropCenterX * Math.cos(rotateRads) - cropCenterY * Math.sin(rotateRads)) * scale;
    const transformedCropCenterY = transformedCenterY + (cropCenterX * Math.sin(rotateRads) + cropCenterY * Math.cos(rotateRads)) * scale;
    
    // Crop dimensions are also scaled
    const transformedCropWidth = cropWidthNatural * scale;
    const transformedCropHeight = cropHeightNatural * scale;
    const transformedCropX = transformedCropCenterX - transformedCropWidth / 2;
    const transformedCropY = transformedCropCenterY - transformedCropHeight / 2;

    // Create final canvas with the cropped image
    canvas.width = cropWidthNatural * pixelRatio;
    canvas.height = cropHeightNatural * pixelRatio;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    // Draw the cropped region from the transformed canvas
    // Ensure we don't go outside canvas bounds
    const sourceX = Math.max(0, Math.round(transformedCropX * pixelRatio));
    const sourceY = Math.max(0, Math.round(transformedCropY * pixelRatio));
    const sourceWidth = Math.min(
      transformedCropWidth * pixelRatio,
      transformedCanvas.width - sourceX
    );
    const sourceHeight = Math.min(
      transformedCropHeight * pixelRatio,
      transformedCanvas.height - sourceY
    );
    
    // If crop goes outside bounds, adjust the destination
    const destX = sourceX < 0 ? Math.abs(transformedCropX) : 0;
    const destY = sourceY < 0 ? Math.abs(transformedCropY) : 0;

    ctx.drawImage(
      transformedCanvas,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      destX,
      destY,
      cropWidthNatural - (destX / pixelRatio),
      cropHeightNatural - (destY / pixelRatio)
    );

    return new Promise<File>((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve(new File([], 'cropped.png'));
            return;
          }
          const file = new File([blob], 'cropped-logo.png', { type: 'image/png' });
          resolve(file);
        },
        'image/png',
        1
      );
    });
  }, [completedCrop, scale, rotate]);

  const handleCropComplete = async () => {
    const croppedFile = await getCroppedImg();
    if (croppedFile) {
      onCropComplete(croppedFile);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 rounded-2xl border border-amber-600/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-zinc-900 border-b border-amber-600/20 p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-amber-50">Crop Logo</h2>
          <button
            onClick={onCancel}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700/50">
            <div className="flex items-center gap-2">
              <ZoomOut className="w-4 h-4 text-zinc-400" />
              <label className="text-sm text-zinc-300">Zoom:</label>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
                className="w-32"
              />
              <span className="text-sm text-zinc-400 w-12">{scale.toFixed(1)}x</span>
            </div>

            <div className="flex items-center gap-2">
              <RotateCw className="w-4 h-4 text-zinc-400" />
              <label className="text-sm text-zinc-300">Rotate:</label>
              <input
                type="range"
                min="-180"
                max="180"
                step="1"
                value={rotate}
                onChange={(e) => setRotate(Number(e.target.value))}
                className="w-32"
              />
              <span className="text-sm text-zinc-400 w-16">{rotate}°</span>
            </div>
          </div>

          {/* Crop Area */}
          <div className="flex justify-center">
            <div className="relative">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                minWidth={50}
                minHeight={50}
                className="max-w-full"
              >
                <img
                  ref={imgRef}
                  alt="Crop me"
                  src={imageSrc}
                  style={{
                    transform: `scale(${scale}) rotate(${rotate}deg)`,
                    maxWidth: '100%',
                    maxHeight: '70vh',
                  }}
                  onLoad={onImageLoad}
                />
              </ReactCrop>
            </div>
          </div>

          {/* Hidden canvas for processing */}
          <canvas ref={canvasRef} className="hidden" />

          {/* Instructions */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <p className="text-sm text-amber-200">
              <strong>Instructions:</strong> Drag the corners and edges to adjust the crop area freely. Use zoom and rotate controls to position your logo perfectly. The logo will maintain its original aspect ratio and be converted to PNG format.
              {aspect !== undefined && (
                <span className="block mt-1 text-amber-300/80">
                  Aspect ratio is locked to {aspect > 1 ? `${aspect.toFixed(2)}:1` : `1:${(1/aspect).toFixed(2)}`} for {aspect > 1 ? 'horizontal' : aspect < 1 ? 'vertical' : 'square'} logos.
                </span>
              )}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-700/50">
            <button
              onClick={onCancel}
              className="px-6 py-2.5 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleCropComplete}
              disabled={!completedCrop}
              className="px-6 py-2.5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white rounded-lg transition-all font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Check className="w-5 h-5" />
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

